import { Image } from "@winamp/icons/Image.js";
import { Suspense, lazy, memo, useState } from "react";
import type { Area, Size } from "react-easy-crop";
import { ClientOnly } from "../ClientOnly";
import { InputRange } from "../InputRange";

interface ICrop {
  x: number;
  y: number;
}

export interface IImageCropperContext {
  image?: string;
  zoom?: number;
  crop: ICrop;
  setZoom?: (zoom: number) => void;
  setCrop: (crop: ICrop) => void;
  setCroppedAreaPixels?: (croppedAreaPixels: Area) => void;
}

export interface IImageCropperProps {
  aspect?: number;
  cropShape?: "rect" | "round";
  minSize?: Size;
  context?: IImageCropperContext;
}

const LazyImageCropper = lazy(async () => {
  const { default: ReactEasyCrop } = await import("react-easy-crop");

  return {
    default: function ImageCropper({
      aspect = 1,
      cropShape,
      minSize,
      context: { zoom, crop, setZoom, setCrop, setCroppedAreaPixels, image } = {
        crop: { x: 0, y: 0 },
        setCrop: () => {},
      },
    }: IImageCropperProps) {
      const [maxZoom, setMaxZoom] = useState(minSize ? 1 : 3);

      const onCropComplete = (_croppedArea: Area, croppedAreaPixels: Area) => {
        if (minSize) {
          setCroppedAreaPixels?.({
            ...croppedAreaPixels,
            width: Math.min(croppedAreaPixels.width, minSize.width),
            height: Math.min(croppedAreaPixels.height, minSize.height),
          });
        } else {
          setCroppedAreaPixels?.(croppedAreaPixels);
        }
      };

      return (
        <div className="flex flex-col gap-6">
          <div className="relative max-h-[calc(100vh_-_258px)] md:max-h-[calc(80vh_-_278px)] h-[440px] rounded-lg overflow-hidden">
            <ReactEasyCrop
              image={image}
              crop={crop}
              zoom={zoom}
              maxZoom={maxZoom}
              aspect={aspect}
              onMediaLoaded={(mediaSize) => {
                if (minSize) {
                  const maxHeightZoom =
                    mediaSize.naturalHeight / minSize.height;
                  const maxWidthZoom = mediaSize.naturalWidth / minSize.width;

                  setMaxZoom(Math.min(maxHeightZoom, maxWidthZoom));
                }

                setCrop?.({ x: 0, y: 0 });
                setZoom?.(1);
              }}
              onCropChange={setCrop}
              onCropComplete={onCropComplete}
              onZoomChange={setZoom}
              cropShape={cropShape}
              classes={{
                containerClassName: "bg-surface-overlay",
              }}
            />
          </div>

          <div className="flex gap-4 items-center">
            <Image className="size-6 text-primary" />
            <InputRange
              disabled={maxZoom <= 1}
              onChange={(e) => setZoom?.(Number(e.target.value))}
              value={zoom}
              max={maxZoom}
              min={1}
              step={0.01}
              aria-label="value"
            />
            <Image className="size-8 text-primary" />
          </div>
        </div>
      );
    },
  };
});

const MemoImageCropper = memo(LazyImageCropper);

export function ImageCropper(props: IImageCropperProps) {
  return (
    <section className="flex flex-col gap-6 rounded-xl border border-primary p-6">
      <ClientOnly fallback={<div />}>
        <Suspense fallback={<div />}>
          <MemoImageCropper {...props} />
        </Suspense>
      </ClientOnly>
    </section>
  );
}
